import React, { useRef, useEffect, useState } from 'react';
import { FiSend, FiMic, FiMicOff } from 'react-icons/fi';
import NeonButton from '../Common/Button';
import { getQuestionsForPhase } from '../../services/phaseQuestions';
import './Chat.css';
import { useLanguage } from '../../context/LanguageContext';

const ChatInput = ({
  inputText,
  onInputChange,
  onSend,
  onQuickQuestion,
  currentPhase
}) => {
  const { language, t } = useLanguage();
  const textareaRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = { en: 'en-US', ru: 'ru-RU', uz: 'uz-UZ' }[language] || 'en-US';

      recognitionRef.current.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            transcript += event.results[i][0].transcript + ' ';
          }
        }
        if (transcript) {
          onInputChange(prev => prev + transcript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => setIsListening(false);
    }
  }, [onInputChange, language]);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert(t('speechUnsupported'));
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error("Failed to start speech recognition:", error);
        setIsListening(false);
      }
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [inputText]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend(e);
    }
  };

  const quickQuestions = currentPhase === 'Anamnesis'
    ? getQuestionsForPhase(currentPhase, language)
    : [];

  return (
    <div className="input-area">
      <form className="chat-input" onSubmit={onSend}>
        {quickQuestions.length > 0 && (
          <div className="quick-questions">
            <span className="quick-label">{t('suggestedPrompts')}</span>
            <div className="quick-buttons">
              {quickQuestions.slice(0, 4).map((q, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="quick-btn"
                  onClick={() => onQuickQuestion(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="input-wrapper">
          <textarea
            ref={textareaRef}
            value={inputText}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isListening ? t('listening') : (
              currentPhase === 'Anamnesis'
                ? t('askPatient')
                : currentPhase === 'Physical Exam'
                  ? t('requestExam')
                  : t('clinicalQuestion')
            )}
            className={`message-input ${isListening ? 'listening' : ''}`}
            rows="1"
          />
          <div className="input-actions">
            <button
              type="button"
              className={`action-btn mic-btn ${isListening ? 'listening' : ''}`}
              onClick={toggleListening}
              title={isListening ? t('stopListening') : t('startVoice')}
            >
              {isListening ? <FiMicOff size={20} /> : <FiMic size={20} />}
            </button>
            <NeonButton
              type="submit"
              variant="primary"
              disabled={!inputText.trim()}
              className="send-btn"
            >
              <FiSend size={20} />
              <span>{t('send')}</span>
            </NeonButton>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChatInput;
